import { useState, useCallback } from 'react';
import { SimpleFiltersObjectValues, SimpleFiltersObjectType } from '@britishcouncil/react-admin';
import { useCentresOptions } from './useCentresOptions';
import { useCountriesOptions } from './useCountriesOptions';
import { useRegionsOptions } from './useRegionsOptions';
import { LocationFilterType } from 'types';

type UseLocationFiltersCascadeResult<
  TFilters extends SimpleFiltersObjectType,
  TFilterState extends LocationFilterType,
> = {
  filtersValues: SimpleFiltersObjectValues<TFilters> & TFilterState;
  regionsOptions: ReturnType<typeof useRegionsOptions>;
  countriesOptions: ReturnType<typeof useCountriesOptions>;
  centresOptions: ReturnType<typeof useCentresOptions>;
  onFiltersApply: (filtersValues: SimpleFiltersObjectValues<TFilters> & TFilterState) => void;
  onFiltersChange: (filtersValues: SimpleFiltersObjectValues<TFilters> & TFilterState) => void;
};

export const useLocationFiltersCascade = <
  TFilters extends SimpleFiltersObjectType,
  TFilterState extends LocationFilterType = LocationFilterType,
>(
  initialFilterState: SimpleFiltersObjectValues<TFilters> & TFilterState,
  setFilterState: (filterState: TFilterState) => void
): UseLocationFiltersCascadeResult<TFilters, TFilterState> => {
  const [filtersValues, setFiltersValues] = useState<
    SimpleFiltersObjectValues<TFilters> & TFilterState
  >(initialFilterState);

  const regionsOptions = useRegionsOptions();
  const countriesOptions = useCountriesOptions(filtersValues.regionsIds);
  const centresOptions = useCentresOptions(
    filtersValues.centreType,
    filtersValues.countriesIds,
    filtersValues.regionsIds
  );

  const onFiltersApply = useCallback(
    (filtersValues: SimpleFiltersObjectValues<TFilters> & TFilterState) => {
      setFilterState(filtersValues);
    },
    [setFilterState]
  );

  const onFiltersChange = useCallback(
    (filtersValues: SimpleFiltersObjectValues<TFilters> & TFilterState) => {
      setFiltersValues((currentFilters) => {
        const current = currentFilters as LocationFilterType;
        const next = filtersValues as LocationFilterType;

        const clearRegions = current.centreType !== next.centreType;
        const clearCountries = clearRegions || current.regionsIds !== next.regionsIds;
        const clearCentres = clearCountries || current.countriesIds !== next.countriesIds;

        return {
          ...filtersValues,
          regionsIds: clearRegions ? [] : next.regionsIds,
          countriesIds: clearCountries ? [] : next.countriesIds,
          centresIds: clearCentres ? [] : next.centresIds,
        };
      });
    },
    []
  );

  return {
    filtersValues,
    regionsOptions,
    countriesOptions,
    centresOptions,
    onFiltersApply,
    onFiltersChange,
  };
};
